import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

import { getCustomers } from "src/api/customer";
import { addInvoiceGroupMembers, getInvoiceGroupMembers, removeInvoiceGroupMembers } from "src/api/invoice";
import { getSubAccounts } from "src/api/subAccount";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import CustomModal from "src/components/CustomModal";
import { DataTable, type Column } from "src/components/common";
import { Button } from "src/components/ui/button";
import { Checkbox } from "src/components/ui/checkbox";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { showSuccessToast } from "src/lib/utils";

/** A member row — customers and sub accounts are shaped the same for this list. */
interface MemberRow {
	id: number | string;
	first_name?: string;
	last_name?: string;
}

/** The request body key depends on the tenant type: schools group sub accounts, everyone else groups customers. */
type MemberKey = "subaccounts" | "customers";

interface MemberMutation {
	uuid: string;
	data: Record<MemberKey, (number | string)[]>;
}

const nameOf = (row: MemberRow) => `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "—";

interface InvoiceGroupMembersProps {
	uuid: string;
}

const InvoiceGroupMembers = ({ uuid: groupUuid }: InvoiceGroupMembersProps) => {
	const { user } = useAuth();
	const memberKey: MemberKey = user?.organization_type_name === "Schools" ? "subaccounts" : "customers";

	return (
		<div className="tw-grid tw-h-fit tw-gap-4">
			<div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
				<h3 className="tw-text-[15px] tw-font-semibold tw-text-text">Members</h3>

				<CustomModal
					title="Add members"
					size="normal"
					trigger={
						<Button type="button" size="sm" variant="secondary">
							Add members
						</Button>
					}
				>
					<AddMembersForm groupUuid={groupUuid} memberKey={memberKey} />
				</CustomModal>
			</div>

			<CurrentMembers groupUuid={groupUuid} memberKey={memberKey} />
		</div>
	);
};

const CurrentMembers = ({ groupUuid, memberKey }: { groupUuid: string; memberKey: MemberKey }) => {
	const { data, isLoading, refetch } = useQuery({
		queryKey: ["invoice-group-members", groupUuid],
		queryFn: () => getInvoiceGroupMembers({ uuid: groupUuid }),
	});

	const { mutate } = useMutation(removeInvoiceGroupMembers, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			refetch();
		},
	});

	const columns: Column<MemberRow>[] = [
		{ key: "sn", header: "S/N", className: "tw-w-[64px]", cell: (_row, index) => index + 1 },
		{ key: "name", header: "Name", primary: true, cell: (row) => nameOf(row) },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[64px] tw-text-right",
			cell: (row) => (
				<CustomConfirmDialog
					header="Remove this member?"
					subHeader="They will stop receiving invoices sent to this group."
					confirmButtonText="Yes, remove"
					trigger={
						<button
							type="button"
							aria-label={`Remove ${nameOf(row)}`}
							className="tw-rounded-lg tw-p-1.5 tw-text-red tw-transition-colors hover:tw-bg-red-tint"
						>
							<Trash2 className="tw-h-3.5 tw-w-3.5" />
						</button>
					}
					mutateFn={() => mutate({ uuid: groupUuid, data: { [memberKey]: [row.id] } as MemberMutation["data"] })}
				/>
			),
		},
	];

	return (
		<DataTable
			columns={columns}
			data={data?.data?.data ?? []}
			loading={isLoading}
			rowKey={(row) => row.id}
			emptyTitle="No members yet"
			emptyDescription="Add customers to this group to invoice them together."
		/>
	);
};

/**
 * Customers expose `first_name`/`last_name`; sub accounts expose `firstname`/`lastname`.
 * Normalising here is why the schools variant of this list used to render "undefined undefined".
 */
const fetchCandidates = async (memberKey: MemberKey): Promise<MemberRow[]> => {
	if (memberKey === "subaccounts") {
		const payload = await getSubAccounts({});

		return (payload.data?.data ?? []).map(({ id, firstname, lastname }) => ({
			id: id ?? "",
			first_name: firstname,
			last_name: lastname,
		}));
	}

	const payload = await getCustomers({});

	return (payload.data?.data ?? []).map(({ id, first_name, last_name }) => ({ id: id ?? "", first_name, last_name }));
};

const AddMembersForm = ({ groupUuid, memberKey }: { groupUuid: string; memberKey: MemberKey }) => {
	const closePortal = usePortalContext();
	const queryClient = useQueryClient();

	const [selected, setSelected] = useState<(number | string)[]>([]);

	const { data: candidates, isLoading } = useQuery({
		queryKey: ["group-members-list", memberKey],
		queryFn: () => fetchCandidates(memberKey),
	});

	const { mutate, isLoading: isSaving } = useMutation(addInvoiceGroupMembers, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries(["invoice-group-members"]);
			closePortal();
		},
	});

	const toggle = (id: number | string) =>
		setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

	const columns: Column<MemberRow>[] = [
		{
			key: "select",
			header: "",
			className: "tw-w-[52px]",
			cell: (row) => (
				<Checkbox
					checked={selected.includes(row.id)}
					onCheckedChange={() => toggle(row.id)}
					aria-label={`Select ${nameOf(row)}`}
				/>
			),
		},
		{ key: "sn", header: "S/N", className: "tw-w-[64px]", cell: (_row, index) => index + 1 },
		{ key: "name", header: "Name", primary: true, cell: (row) => nameOf(row) },
	];

	return (
		<form
			onSubmit={(event) => {
				event.preventDefault();
				if (selected.length) mutate({ uuid: groupUuid, data: { [memberKey]: selected } as MemberMutation["data"] });
			}}
			className="tw-grid tw-gap-4"
		>
			<DataTable
				columns={columns}
				data={candidates ?? []}
				loading={isLoading}
				rowKey={(row) => row.id}
				emptyTitle="Nobody to add"
				emptyDescription="Create a customer first, then add them to this group."
			/>

			<Button type="submit" variant="secondary" className="tw-w-full" disabled={isSaving || selected.length === 0}>
				{isSaving ? "Adding..." : selected.length ? `Add ${selected.length} member${selected.length > 1 ? "s" : ""}` : "Add members"}
			</Button>
		</form>
	);
};

export default InvoiceGroupMembers;
